// Answer Assist - Exam Mode state + audit log helpers.
//
// Exam Mode is the JCQ-aligned locked-down attempt mode that lets MDT be used
// as an exam access arrangement (computer reader / scribe replacement). When a
// session is active:
//   - AI vision pass is DISABLED (manual question-tapping only)
//   - Proper-noun cleanup is DISABLED
//   - Question simplification is DISABLED
//   - Word prediction is DISABLED
//   - Premium voices are DISABLED (Web Speech only - no internet text leaves device)
//   - Settings is locked
//   - Tools and History screens redirect back to the active exam
//
// Every meaningful action writes an audit event to public.exam_audit_events on the
// server (server-side ts, append-only). That audit trail is what schools hand to
// JCQ if challenged about the candidate's access arrangement.

import { supabase } from './supabase';

export interface ExamSessionMeta {
  id: string;
  candidateName: string;
  candidateNumber: string;
  schoolName: string;
  paperTitle: string;
  startedAt: string;
}

export type ExamAuditEventType =
  | 'session_started'
  | 'session_ended'
  | 'worksheet_captured'
  | 'worksheet_uploaded'
  | 'question_displayed'
  | 'question_read_aloud'
  | 'answer_typed'
  | 'answer_dictated'
  | 'answer_saved'
  | 'worksheet_exported_pdf'
  | 'attempt_paused'
  | 'attempt_resumed'
  | 'feature_blocked';

const STORAGE_KEY_ACTIVE_SESSION = 'mdt_exam_session_active';

interface StoredSession {
  session: ExamSessionMeta;
  // Lock signature - changes if a tamperer toggles the key. The server's locked
  // boolean is the authoritative source; this is just a redundancy.
  lockNonce: string;
}

export function getActiveExamSession(): ExamSessionMeta | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE_SESSION);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed?.session ?? null;
  } catch {
    return null;
  }
}

export function isExamModeActive(): boolean {
  return getActiveExamSession() !== null;
}

export function setActiveExamSession(session: ExamSessionMeta | null): void {
  try {
    if (!session) {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_SESSION);
      return;
    }
    const stored: StoredSession = {
      session,
      lockNonce: crypto.randomUUID(),
    };
    localStorage.setItem(STORAGE_KEY_ACTIVE_SESSION, JSON.stringify(stored));
  } catch {
    // Storage unavailable - the server's locked flag still enforces the rule.
  }
}

/**
 * Start a new exam session via the server. Returns the session metadata if accepted.
 * The server writes the session row + the initial 'session_started' audit event.
 */
export async function startExamSession(input: {
  candidateName: string;
  candidateNumber: string;
  schoolName: string;
  paperTitle: string;
}): Promise<ExamSessionMeta> {
  const { data: { session: authSession } } = await supabase.auth.getSession();
  if (!authSession?.access_token) {
    throw new Error('Not signed in. Sign in to start an exam session.');
  }
  const response = await fetch('/api/exam-audit', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${authSession.access_token}`,
    },
    body: JSON.stringify({
      action: 'start',
      ...input,
      device_info:
        typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : 'unknown',
    }),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not start exam session: ${response.status} ${detail.slice(0, 200)}`);
  }
  const body = (await response.json()) as { session: ExamSessionMeta };
  setActiveExamSession(body.session);
  return body.session;
}

/**
 * Log an audit event during an active exam session. Fails open in dev mode
 * (the client still functions even if the audit call drops) but the server-side
 * locked flag still forces real sessions to flow through here.
 */
export async function logExamAuditEvent(
  eventType: ExamAuditEventType,
  metadata?: Record<string, unknown>
): Promise<void> {
  const session = getActiveExamSession();
  if (!session) return;
  const { data: { session: authSession } } = await supabase.auth.getSession();
  if (!authSession?.access_token) return;
  try {
    await fetch('/api/exam-audit', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${authSession.access_token}`,
      },
      body: JSON.stringify({
        action: 'event',
        session_id: session.id,
        event_type: eventType,
        metadata: metadata ?? null,
      }),
    });
  } catch {
    // Best effort. The session itself remains locked server-side.
  }
}

/** End the active exam session and clear the local lock. */
export async function endExamSession(input: {
  endReason:
    | 'completed'
    | 'abandoned_by_candidate'
    | 'abandoned_by_invigilator'
    | 'time_expired'
    | 'technical_fault';
}): Promise<void> {
  const session = getActiveExamSession();
  if (!session) return;
  const { data: { session: authSession } } = await supabase.auth.getSession();
  if (!authSession?.access_token) {
    setActiveExamSession(null);
    return;
  }
  try {
    await fetch('/api/exam-audit', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${authSession.access_token}`,
      },
      body: JSON.stringify({
        action: 'end',
        session_id: session.id,
        end_reason: input.endReason,
      }),
    });
  } finally {
    setActiveExamSession(null);
  }
}

/**
 * Universal helper used by AI-feature surfaces. Returns true if the feature is
 * allowed right now; if false, logs an audit event so an attempt to bypass is
 * recorded against the candidate's session.
 */
export async function assertAiFeatureAllowed(feature: string): Promise<boolean> {
  if (!isExamModeActive()) return true;
  await logExamAuditEvent('feature_blocked', { feature });
  return false;
}
